// Where people stop in the card request (pr/card/dropoff.py). The form notes what it does on each visit: the welcome
// screen, Start, each screen seen and finished, Back, Skip, the question in use, each error (a short code, never the
// message or what was typed), Send, sent, and leaving the page. Notes go up in small batches with fetch keepalive (it
// carries the CSRF header, which sendBeacon can't). Nothing here ever waits or throws: the form never notices it.
import { useEffect, useRef } from 'react'
import { readCookie } from '../../lib/api'

export type TrackKind = 'welcome' | 'start' | 'screen_view' | 'screen_done' | 'back' | 'skip' | 'field_focus'
  | 'field_error' | 'error' | 'send' | 'sent' | 'leave'
type Note = { kind: TrackKind; screen?: string | null; screen_no?: number | null; field?: string | null; code?: string | null; t: number }

const newVisit = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`)
const visit = newVisit()
let queue: Note[] = []
let timer: number | undefined
let where: { screen: string | null; screen_no: number | null } = { screen: null, screen_no: null }
let ctx: { applicationId: string | null; off: boolean } = { applicationId: null, off: false }
let listening = false
let left = false

/** The request once it exists; `off` while staff view as a company (its writes are refused). */
export function trackContext(c: Partial<typeof ctx>) { ctx = { ...ctx, ...c } }

export function track(kind: TrackKind, extra: Omit<Partial<Note>, 'kind' | 't'> = {}) {
  try {
    if (ctx.off) return
    listen()
    if ((kind === 'welcome' || kind === 'screen_view') && !left && where.screen === (extra.screen ?? null)) return   // the same screen again
    if (kind === 'welcome' || kind === 'screen_view') { where = { screen: extra.screen ?? null, screen_no: extra.screen_no ?? null }; left = false; views++ }
    queue.push({ kind, screen: where.screen, screen_no: where.screen_no, ...extra, t: Date.now() })
    if (kind === 'sent') where = { screen: null, screen_no: null }     // done: nothing left to leave
    if (kind === 'leave' || kind === 'sent' || kind === 'send' || queue.length >= 20) flush()
    else if (timer === undefined) timer = window.setTimeout(flush, 3000)
  } catch { /* never in the way */ }
}

/** Notes each error as it appears under a question: the question and a short code, never the sentence. `asks` maps
 *  a question to its "answer this" sentence, so a missing answer reads as `required`. */
export function useTrackErrors(errors: Record<string, string>, asks: Record<string, string | undefined>) {
  const seen = useRef<Record<string, string>>({})
  useEffect(() => {
    for (const [field, msg] of Object.entries(errors)) {
      if (seen.current[field] === msg) continue
      track('field_error', { field, code: msg === asks[field] || /^(Required\.|Answer this to go on\.)$/.test(msg) ? 'required' : reasonOf(msg) })
    }
    seen.current = { ...errors }
  }, [errors]) // eslint-disable-line react-hooks/exhaustive-deps
}

/** The page is going (closed, reloaded, another app on a phone). */
export function trackLeave() { if (!left && where.screen) { left = true; track('leave') } }

let views = 0
/** The form left the page inside the app (a link, the logo). Moving on from screen 2 to screen 3 swaps the form too,
 *  so wait to see whether a new screen shows. */
export function trackGone() {
  const n = views
  window.setTimeout(() => { if (views === n) trackLeave() }, 2500)
}

function listen() {
  if (listening) return
  listening = true
  window.addEventListener('pagehide', trackLeave)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') trackLeave()
    else if (left && where.screen) track(where.screen === 'welcome' ? 'welcome' : 'screen_view', { ...where })   // back again
  })
}

function flush() {
  window.clearTimeout(timer)
  timer = undefined
  if (!queue.length) return
  const events = queue.splice(0, 50)
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const csrf = readCookie('pr_csrf')
  if (csrf) headers['X-PR-CSRF'] = csrf
  try {
    fetch('/api/card/events', { method: 'POST', headers, credentials: 'same-origin', keepalive: true,
      body: JSON.stringify({ visit, application_id: ctx.applicationId, sent_at: Date.now(), events }) }).catch(() => {})
  } catch { /* never in the way */ }
  if (queue.length) flush()
}

// Why an answer was refused, as a short code. The sentence can quote the answer (a ZIP), so only the code goes up.
const REASONS: [string, RegExp][] = [
  ['special', /special characters/i], ['too_long', /too long/i], ['tax_id_in_text', /tax IDs/i], ['website', /web address|website/i],
  ['ein', /9 digits|EIN/], ['phone_fake', /not a real US phone/i], ['phone', /phone|10-digit/i], ['email', /email/i],
  ['amount', /dollar amount|whole number/i], ['zip_state', /ZIP .* is not in/i], ['zip', /ZIP/], ['po_box', /PO box/i],
  ['street', /street/i], ['city', /city/i], ['state', /US state/i], ['pick', /pick|check the box|confirm/i],
  ['owner', /^Owner \d|person \d/i], ['company_exists', /already on ernd/i],
]
export function reasonOf(message: string): string {
  for (const [code, re] of REASONS) if (re.test(message)) return code
  return 'other'
}
